import React from 'react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const fmt = (v, suffix = '', prefix = '') => (v === null || v === undefined ? '—' : `${prefix}${Number(v).toLocaleString()}${suffix}`);
const day = (d) => (d ? format(parseISO(d), 'd MMM yyyy') : '?');
const STATUS_LABELS = {
  ACTIVE: 'Live', PAUSED: 'Paused', ADSET_PAUSED: 'Ad set paused', CAMPAIGN_PAUSED: 'Campaign paused',
  WITH_ISSUES: 'With issues', DISAPPROVED: 'Disapproved', ARCHIVED: 'Archived', DELETED: 'Deleted',
  PENDING_REVIEW: 'In review', IN_PROCESS: 'Processing', UNAVAILABLE: 'Unavailable',
};
const statusSummary = (statuses = {}) => {
  const counts = {};
  Object.values(statuses).forEach((s) => { counts[s] = (counts[s] || 0) + 1; });
  return Object.entries(counts).map(([s, n]) => `${n} ${STATUS_LABELS[s] || s.toLowerCase()}`).join(' · ');
};
export const runRange = (start, end, live) => `${day(start)} → ${live ? 'Live now' : day(end)}`;

const LiveDot = ({ live }) => (
  <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${live ? 'bg-emerald-500' : 'bg-slate-300'}`}>
    {live && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />}
  </span>
);

export const MetaAccountChips = ({ metrics }) => {
  const accs = Object.entries(metrics?.meta_accounts || {});
  if (!accs.length) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <div className="mt-1 flex flex-wrap gap-1" data-testid="meta-account-chips">
        {accs.map(([id, a]) => (
          <Tooltip key={id}>
            <TooltipTrigger asChild>
              <span
                className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${id === metrics.meta_primary_account ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}
                data-testid={`meta-account-chip-${id}`}
              >
                {a.live !== undefined && <LiveDot live={a.live} />}
                {a.account_name}{a.roas != null && <span className="font-bold">{a.roas}x</span>}
              </span>
            </TooltipTrigger>
            <TooltipContent className="text-xs">
              <div className="font-semibold mb-1">{a.account_name} <span className="font-normal opacity-70">{id}</span></div>
              <div>ROAS {fmt(a.roas, 'x')} · Spend {fmt(a.spend, '', '₹')}</div>
              <div>CTR {fmt(a.ctr, '%')} · CPA {fmt(a.cpa, '', '₹')} · CVR {fmt(a.cvr, '%')}</div>
              <div>Impressions {fmt(a.impressions)}</div>
              {a.ad_statuses && (
                <div className="mt-1 border-t border-white/20 pt-1">
                  <div>
                    {a.live ? `Live · ${a.live_ads} of ${Object.keys(a.ad_statuses).length} ads running` : 'Not live'}
                    {Object.values(a.ad_statuses).some((s) => s !== 'ACTIVE') && ` — ${statusSummary(a.ad_statuses)}`}
                  </div>
                  <div>Ran {runRange(a.run_start, a.run_end, a.live)} <span className="opacity-70">(from ad schedule)</span></div>
                </div>
              )}
              {id === metrics.meta_primary_account && <div className="mt-1 opacity-80">Shown in the ROAS / Spend columns (highest spend)</div>}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
};

export const MetaRunInfo = ({ metrics }) => {
  if (!metrics?.meta_synced_at) return null;
  const hasStatus = metrics.meta_live !== undefined;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-slate-500" data-testid="meta-run-info">
      {hasStatus && (
        <span
          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-semibold ${metrics.meta_live ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
          data-testid="meta-live-badge"
        >
          <LiveDot live={metrics.meta_live} />{metrics.meta_live ? 'LIVE' : 'Not live'}
        </span>
      )}
      {hasStatus && metrics.meta_run_start && <span data-testid="meta-run-range">{runRange(metrics.meta_run_start, metrics.meta_run_end, metrics.meta_live)}</span>}
      <span className="text-slate-400" title={`Lifetime numbers · synced ${metrics.meta_synced_at}`}>
        · synced {formatDistanceToNow(new Date(metrics.meta_synced_at))} ago
      </span>
    </div>
  );
};
